const ATTRIBUTE_KEY = '_cart_drawer_width';
const SAVED_WIDTH_VARIABLE = '--drawer-width-saved';
const DEFAULT_WIDTH = 500;
const MIN_WIDTH = 360;
const MAX_WIDTH = 800;
const DESKTOP_MEDIA_QUERY = '(min-width: 1201px)';
const RESIZER_SELECTOR = '[data-drawer-resizer]';

class DrawerResizer {
  constructor() {
    this.handleEl = null;
    this.pointerId = null;
    this.startX = 0;
    this.startWidth = 0;
    this.pendingWidth = null;
    this.frameId = null;
    this.widthChanged = false;
    this.removalObserver = null;

    this.flushPendingWidth = this.flushPendingWidth.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.stopResize = this.stopResize.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.stopResizerClick = this.stopResizerClick.bind(this);
    document.addEventListener('pointerdown', this.handlePointerDown, true);
    document.addEventListener('click', this.stopResizerClick, true);
  }

  getShopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  clampWidth(width) {
    const max = Math.min(MAX_WIDTH, window.innerWidth * 0.5);
    return Math.max(MIN_WIDTH, Math.min(max, Math.round(width)));
  }

  setWidth(width) {
    document.documentElement.style.setProperty(SAVED_WIDTH_VARIABLE, `${width}px`);
  }

  getCurrentWidth() {
    const computed = getComputedStyle(document.documentElement)
      .getPropertyValue(SAVED_WIDTH_VARIABLE)
      .trim();
    const parsed = parseInt(computed, 10);
    return Number.isFinite(parsed) ? this.clampWidth(parsed) : this.clampWidth(DEFAULT_WIDTH);
  }

  persistWidth(width) {
    try {
      window.fetch(`${this.getShopifyRoot()}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [ATTRIBUTE_KEY]: String(width) } }),
        keepalive: true,
      }).catch(() => {});
    } catch (error) {}
  }

  isDesktop() {
    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
  }

  flushPendingWidth() {
    this.frameId = null;
    if (this.pendingWidth === null) return;
    this.setWidth(this.pendingWidth);
    this.pendingWidth = null;
  }

  scheduleWidth(width) {
    this.pendingWidth = width;
    if (this.frameId !== null) return;
    this.frameId = window.requestAnimationFrame(this.flushPendingWidth);
  }

  removeActiveListeners() {
    document.removeEventListener('pointermove', this.handlePointerMove, true);
    document.removeEventListener('pointerup', this.stopResize, true);
    document.removeEventListener('pointercancel', this.stopResize, true);
    document.removeEventListener('lostpointercapture', this.stopResize, true);
    window.removeEventListener('blur', this.stopResize);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  stopResize(event) {
    if (this.pointerId === null) return;
    if (event && event.pointerId !== undefined && event.pointerId !== this.pointerId) return;

    const handleEl = this.handleEl;
    const pointerId = this.pointerId;
    const widthChanged = this.widthChanged;

    this.removeActiveListeners();
    if (this.removalObserver) {
      this.removalObserver.disconnect();
      this.removalObserver = null;
    }

    if (this.frameId !== null) {
      window.cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
    this.flushPendingWidth();

    if (handleEl && handleEl.isConnected) {
      try {
        handleEl.releasePointerCapture(pointerId);
      } catch (error) {}
    }

    document.documentElement.classList.remove('drawer-resizing');

    if (widthChanged && this.isDesktop()) {
      this.persistWidth(this.getCurrentWidth());
      window.dispatchEvent(new Event('resize'));
    }

    this.handleEl = null;
    this.pointerId = null;
    this.widthChanged = false;
  }

  handlePointerMove(event) {
    if (event.pointerId !== this.pointerId) return;
    if (!this.handleEl || !this.handleEl.isConnected) {
      this.stopResize();
      return;
    }

    const delta = this.startX - event.clientX;
    const nextWidth = this.clampWidth(this.startWidth + delta);
    this.widthChanged = this.widthChanged || nextWidth !== this.startWidth;
    this.scheduleWidth(nextWidth);
  }

  handleVisibilityChange() {
    if (document.hidden) this.stopResize();
  }

  getResizerFromEvent(event) {
    const target = event.target;
    return target && typeof target.closest === 'function'
      ? target.closest(RESIZER_SELECTOR)
      : null;
  }

  handlePointerDown(event) {
    const handleEl = this.getResizerFromEvent(event);
    if (!handleEl || this.pointerId !== null || !this.isDesktop()) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();
    this.handleEl = handleEl;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startWidth = this.getCurrentWidth();
    this.widthChanged = false;

    try {
      handleEl.setPointerCapture(this.pointerId);
    } catch (error) {
      this.stopResize();
      return;
    }

    document.documentElement.classList.add('drawer-resizing');
    document.addEventListener('pointermove', this.handlePointerMove, true);
    document.addEventListener('pointerup', this.stopResize, true);
    document.addEventListener('pointercancel', this.stopResize, true);
    document.addEventListener('lostpointercapture', this.stopResize, true);
    window.addEventListener('blur', this.stopResize);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    if (document.body) {
      this.removalObserver = new MutationObserver(() => {
        if (!handleEl.isConnected) this.stopResize();
      });
      this.removalObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  stopResizerClick(event) {
    if (this.getResizerFromEvent(event)) event.stopPropagation();
  }
}

new DrawerResizer();

window.wetheme?.webcomponentRegistry?.register({ key: 'cart-drawer-resizer' });
